(() => {
  if (document.documentElement.hasAttribute('data-promo-clear-loaded')) return;
  document.documentElement.setAttribute('data-promo-clear-loaded', '');

  let lastLightboxTrigger = null;
  let previousBodyOverflow = '';
  let lightboxOpen = false;
  let scrollFrame = false;
  let observedSubmit = null;
  let submitObserver = null;
  let lastSecondaryAdd = null;
  let secondaryAddStarted = 0;
  let mainSectionId = null;
  let lightboxHostSectionId = null;
  let lightboxTriggerSectionId = null;

  const getSectionId = (element) => element?.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') || null;

  const getLightbox = (createIfMissing = false) => {
    const mainLightbox = document.querySelector('.section-promo-clear-main #promo-clear-lightbox');
    let generated = document.querySelector('[data-clear-lightbox-generated]');
    if (mainLightbox && generated && mainLightbox !== generated) {
      if (lightboxOpen && !generated.hidden) return generated;
      generated.remove();
      generated = null;
    }
    let lightbox = mainLightbox || generated || document.getElementById('promo-clear-lightbox');
    if (!lightbox && createIfMissing) {
      lightbox = document.createElement('div');
      lightbox.id = 'promo-clear-lightbox';
      lightbox.className = 'promo-clear promo-clear-lightbox';
      lightbox.hidden = true;
      lightbox.dataset.clearLightboxGenerated = 'true';
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', 'Image viewer');
      const backdrop = document.createElement('div');
      backdrop.className = 'promo-clear-lightbox__backdrop';
      backdrop.dataset.clearLightboxClose = '';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'promo-clear-lightbox__close';
      close.dataset.clearLightboxClose = '';
      close.setAttribute('aria-label', 'Close image viewer');
      close.textContent = '×';
      const content = document.createElement('div');
      content.className = 'promo-clear-lightbox__content';
      content.dataset.clearLightboxContent = '';
      lightbox.append(backdrop, close, content);
      document.body.appendChild(lightbox);
    }
    return lightbox;
  };

  const closeLightbox = (restoreFocus = true) => {
    if (!lightboxOpen) {
      lastLightboxTrigger = null;
      lightboxHostSectionId = null;
      lightboxTriggerSectionId = null;
      return;
    }
    const lightbox = getLightbox();
    if (lightbox) {
      lightbox.hidden = true;
      lightbox.querySelector('[data-clear-lightbox-content]').replaceChildren();
    }
    lightboxOpen = false;
    document.body.style.overflow = previousBodyOverflow;
    previousBodyOverflow = '';
    if (restoreFocus) {
      const fallback = document.querySelector('.promo-clear-main .product-form__submit') || document.querySelector('.promo-clear-final .product-form__submit');
      const target = lastLightboxTrigger && lastLightboxTrigger.isConnected ? lastLightboxTrigger : fallback;
      if (target) target.focus({ preventScroll: true });
    }
    lastLightboxTrigger = null;
    lightboxHostSectionId = null;
    lightboxTriggerSectionId = null;
    updateSticky();
  };

  const openLightbox = (images, trigger) => {
    const lightbox = getLightbox(true);
    if (!lightbox) return;
    const content = lightbox.querySelector('[data-clear-lightbox-content]');
    content.replaceChildren();
    lastLightboxTrigger = trigger;
    lightboxHostSectionId = getSectionId(lightbox);
    lightboxTriggerSectionId = getSectionId(trigger);
    if (!lightboxOpen) previousBodyOverflow = document.body.style.overflow;

    if (images.length === 1) {
      const image = document.createElement('img');
      image.src = images[0].src;
      image.alt = images[0].alt || '';
      content.appendChild(image);
    } else {
      const pair = document.createElement('div');
      pair.className = 'promo-clear-lightbox__pair';
      images.forEach((item) => {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const caption = document.createElement('figcaption');
        image.src = item.src;
        image.alt = item.alt || '';
        caption.textContent = item.caption || '';
        figure.append(image, caption);
        pair.appendChild(figure);
      });
      content.appendChild(pair);
    }

    lightboxOpen = true;
    lightbox.hidden = false;
    document.body.style.overflow = 'hidden';
    lightbox.querySelector('.promo-clear-lightbox__close').focus({ preventScroll: true });
    updateSticky();
  };

  const syncSecondaryButtons = () => {
    const busy = observedSubmit && observedSubmit.getAttribute('aria-disabled') === 'true';
    document.querySelectorAll('[data-clear-add]').forEach((button) => {
      button.disabled = !observedSubmit || observedSubmit.disabled || busy;
      button.setAttribute('aria-busy', String(Boolean(busy)));
      button.classList.toggle('is-loading', Boolean(busy));
    });
  };

  const observeSubmit = (submit) => {
    if (observedSubmit === submit) {
      syncSecondaryButtons();
      return;
    }
    if (submitObserver) submitObserver.disconnect();
    observedSubmit = submit;
    if (submit) {
      submitObserver = new MutationObserver(syncSecondaryButtons);
      submitObserver.observe(submit, { attributes: true, attributeFilter: ['aria-disabled', 'disabled'] });
    }
    syncSecondaryButtons();
  };

  const syncPurchase = () => {
    const main = document.querySelector('.promo-clear-main');
    if (!main) return;
    const quantity = main.querySelector('.quantity__input');
    const offers = [...main.querySelectorAll('[data-clear-offer]')];
    if (!quantity || !offers.length) return;
    const selected = offers.find((offer) => offer.checked && !offer.disabled) || offers.find((offer) => Number(offer.value) === Number(quantity.value) && !offer.disabled);
    if (!selected) return;
    offers.forEach((offer) => { offer.checked = offer === selected; });
    quantity.value = selected.value;
    const price = selected.dataset.price;
    const mainPrice = main.querySelector('[data-clear-main-price]');
    const comparePrice = main.querySelector('[data-clear-compare-price]');
    const submitPrice = main.querySelector('[data-clear-submit-price]');
    const stickyPrice = document.querySelector('[data-clear-sticky-price]');
    if (mainPrice && mainPrice.textContent !== price) mainPrice.textContent = price;
    const compareText = selected.dataset.comparePrice || '';
    if (comparePrice && comparePrice.textContent !== compareText) comparePrice.textContent = compareText;
    if (submitPrice && submitPrice.textContent !== price) submitPrice.textContent = price;
    if (stickyPrice && stickyPrice.textContent !== price) stickyPrice.textContent = price;
  };

  const prepareProductInfo = () => {
    const info = document.querySelector('.promo-clear-main')?.closest('product-info');
    if (!info || info.dataset.promoClearQuantityUrl) return;
    const fetchQuantityRules = info.fetchQuantityRules?.bind(info);
    if (!fetchQuantityRules) return;
    info.dataset.promoClearQuantityUrl = 'true';
    info.fetchQuantityRules = () => {
      const currentUrl = new URL(window.location.href);
      const view = currentUrl.searchParams.get('view');
      if (!view) return fetchQuantityRules();
      const currentVariantId = info.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;
      const spinner = info.querySelector('.quantity__rules-cart .loading__spinner');
      spinner?.classList.remove('hidden');
      const url = new URL(info.dataset.url, window.location.origin);
      currentUrl.searchParams.forEach((value, key) => url.searchParams.set(key, value));
      url.searchParams.set('variant', currentVariantId);
      url.searchParams.delete('section_id');
      return fetch(url.href)
        .then((response) => {
          if (!response.ok) throw new Error(`Quantity rules request failed: ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          info.updateQuantityRules(info.dataset.section, html);
        })
        .catch((error) => console.error(error))
        .finally(() => spinner?.classList.add('hidden'));
    };
    if (info.cartUpdateUnsubscriber && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      info.cartUpdateUnsubscriber();
      info.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => info.fetchQuantityRules());
    }
  };

  let stickyBodyPadding = null;
  let stickyBasePadding = 0;

  const syncStickySpace = (height = 0) => {
    if (height > 0) {
      if (stickyBodyPadding === null) {
        stickyBodyPadding = document.body.style.paddingBottom;
        stickyBasePadding = parseFloat(getComputedStyle(document.body).paddingBottom) || 0;
      }
      document.body.style.paddingBottom = `${stickyBasePadding + height}px`;
    } else if (stickyBodyPadding !== null) {
      document.body.style.paddingBottom = stickyBodyPadding;
      stickyBodyPadding = null;
      stickyBasePadding = 0;
    }
  };

  function updateSticky() {
    prepareProductInfo();
    syncPurchase();
    const main = document.querySelector('.promo-clear-main');
    if (main) mainSectionId = getSectionId(main);
    const sticky = document.querySelector('[data-clear-sticky]');
    const submit = document.querySelector('.promo-clear-main .product-form__submit');
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    observeSubmit(submit);
    const shouldShow = Boolean(sticky && submit) && !lightboxOpen && (mobile || submit.getBoundingClientRect().bottom < 0);
    if (sticky) sticky.classList.toggle('is-visible', shouldShow);
    syncStickySpace(shouldShow ? sticky.offsetHeight : 0);
  }

  const setGalleryView = (gallery, showProof) => {
    const product = gallery.querySelector('[data-clear-gallery-product]');
    const proof = gallery.querySelector('[data-clear-gallery-proof-panel]');
    const toggle = gallery.querySelector('[data-clear-gallery-proof-toggle]');
    if (!product || !proof || !toggle) return;
    product.hidden = showProof;
    proof.hidden = !showProof;
    toggle.classList.toggle('is-active', showProof);
    toggle.setAttribute('aria-expanded', String(showProof));
    toggle.querySelector('span').textContent = showProof ? toggle.dataset.productLabel : toggle.dataset.proofLabel;
    if (showProof) proof.querySelector('input[type="range"]')?.focus({ preventScroll: true });
    updateSticky();
  };

  document.addEventListener('click', (event) => {
    const proofToggle = event.target.closest('[data-clear-gallery-proof-toggle]');
    if (proofToggle) {
      const gallery = proofToggle.closest('[data-clear-gallery]');
      setGalleryView(gallery, proofToggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const thumb = event.target.closest('[data-clear-gallery-thumb]');
    if (thumb) {
      const gallery = thumb.closest('[data-clear-gallery]');
      const image = gallery.querySelector('[data-clear-gallery-image]');
      const zoom = gallery.querySelector('[data-clear-gallery-zoom]');
      setGalleryView(gallery, false);
      image.src = thumb.dataset.src;
      image.srcset = thumb.dataset.srcset || '';
      image.alt = thumb.dataset.alt || '';
      zoom.dataset.clearLightbox = thumb.dataset.full;
      zoom.dataset.lightboxAlt = thumb.dataset.alt || '';
      gallery.querySelectorAll('[data-clear-gallery-thumb]').forEach((item) => {
        const active = item === thumb;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      return;
    }

    const tab = event.target.closest('[data-clear-case-tab]');
    if (tab) {
      const section = tab.closest('.promo-clear-results');
      const target = tab.dataset.clearCaseId || tab.dataset.clearCaseTab;
      section.querySelectorAll('[data-clear-case-tab]').forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      section.querySelectorAll('[data-clear-case]').forEach((item) => {
        item.hidden = (item.dataset.clearCaseId || item.dataset.clearCase) !== target;
      });
      return;
    }

    const compareZoom = event.target.closest('[data-clear-compare-zoom]');
    if (compareZoom) {
      const activeCase = compareZoom.closest('.promo-clear-results').querySelector('[data-clear-case]:not([hidden])');
      openLightbox([
        { src: activeCase.dataset.beforeFull, alt: activeCase.dataset.beforeAlt, caption: activeCase.dataset.beforeLabel },
        { src: activeCase.dataset.afterFull, alt: activeCase.dataset.afterAlt, caption: activeCase.dataset.afterLabel }
      ], compareZoom);
      return;
    }

    const singleImage = event.target.closest('[data-clear-lightbox]');
    if (singleImage) {
      const containedImage = singleImage.querySelector('img');
      const alt = singleImage.dataset.lightboxAlt || (containedImage ? containedImage.alt : '');
      openLightbox([{ src: singleImage.dataset.clearLightbox, alt }], singleImage);
      return;
    }

    if (event.target.closest('[data-clear-lightbox-close]')) {
      closeLightbox();
      return;
    }

    const addButton = event.target.closest('[data-clear-add]');
    if (addButton) {
      lastSecondaryAdd = addButton;
      secondaryAddStarted = Date.now();
      const submit = document.querySelector('.promo-clear-main .product-form__submit');
      if (submit && !submit.disabled) submit.click();
    }
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-clear-compare] input[type="range"]')) return;
    const compare = event.target.closest('[data-clear-compare]');
    compare.style.setProperty('--position', `${event.target.value}%`);
    event.target.setAttribute('aria-valuetext', `Comparison divider at ${event.target.value} percent`);
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('.promo-clear-main [data-clear-offer]')) return;
    const quantity = document.querySelector('.promo-clear-main .quantity__input');
    if (quantity) quantity.value = event.target.value;
    syncPurchase();
  });

  document.addEventListener('keydown', (event) => {
    if (!lightboxOpen) return;
    const lightbox = getLightbox();
    if (!lightbox) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeLightbox();
    } else if (event.key === 'Tab') {
      event.preventDefault();
      lightbox.querySelector('.promo-clear-lightbox__close').focus();
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      if (event.source !== 'product-form' || !lastSecondaryAdd || Date.now() - secondaryAddStarted > 15000) return;
      lastSecondaryAdd = null;
      const wrapper = document.querySelector('.promo-clear-main .product-form__error-message-wrapper');
      if (!wrapper) return;
      window.requestAnimationFrame(() => {
        wrapper.tabIndex = -1;
        wrapper.focus({ preventScroll: true });
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        wrapper.scrollIntoView({ behavior, block: 'center' });
      });
    });
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'product-form') lastSecondaryAdd = null;
    });
    subscribe(PUB_SUB_EVENTS.quantityUpdate, syncPurchase);
  }

  window.addEventListener('scroll', () => {
    if (scrollFrame) return;
    scrollFrame = true;
    window.requestAnimationFrame(() => {
      scrollFrame = false;
      updateSticky();
    });
  }, { passive: true });
  window.addEventListener('resize', updateSticky);
  document.addEventListener('shopify:section:load', updateSticky);
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;
    if (lightboxOpen && (sectionId === lightboxTriggerSectionId || sectionId === lightboxHostSectionId)) {
      closeLightbox(false);
    }
    if (sectionId === mainSectionId) {
      if (submitObserver) submitObserver.disconnect();
      observedSubmit = null;
      syncSecondaryButtons();
      syncStickySpace();
    }
    window.requestAnimationFrame(updateSticky);
  });
  updateSticky();
})();
